import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemAvatar,
  Checkbox,
  Divider,
  Snackbar,
  Alert,
  Card,
  CardContent,
  CardActions,
  Avatar,
  InputAdornment,
  CardMedia,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Radio,
  RadioGroup,
  Chip,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Close as CloseIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  PhotoCamera as PhotoCameraIcon,
  Videocam as VideocamIcon,
  CloudUpload as CloudUploadIcon,
  ExpandMore as ExpandMoreIcon,
  SmartToy as SmartToyIcon,
  Campaign as CampaignIcon,
  ShoppingCart as ShoppingCartIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ptBR from 'date-fns/locale/pt-BR';
import SocialPostScheduler from './SocialPostScheduler';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { fetchProductsAsync } from '../redux/slices/productSlice';
import AIAssistant from './AIAssistant';

interface Schedule {
  startDate: Date;
  endDate: Date;
}

interface Lead {
  id: string;
  name: string;
  email: string;
  phone: string;
  source: string;
  status: string;
}

interface Product {
  id: string;
  name: string;
  price: number;
  description?: string;
  imageUrl?: string;
}

interface MediaFile {
  id: string;
  file: File;
  type: 'image' | 'video';
  preview: string;
}

interface MarketingCampaign {
  id: string;
  name: string;
  type: 'email' | 'whatsapp';
  content: string;
  schedules: Schedule[];
  status: 'agendado' | 'publicado' | 'falha';
  createdAt: Date;
  product?: Product;
  leads?: Lead[];
  mediaFiles?: MediaFile[];
}

const MarketingScheduler: React.FC = () => {
  const [campaigns, setCampaigns] = useState<MarketingCampaign[]>([]);
  const [newCampaign, setNewCampaign] = useState<Omit<MarketingCampaign, 'id' | 'status'>>({
    name: '',
    type: 'email',
    content: '',
    schedules: [{ startDate: new Date(), endDate: new Date() }],
    createdAt: new Date(),
    mediaFiles: []
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [openLeadDialog, setOpenLeadDialog] = useState(false);
  const [productSearch, setProductSearch] = useState('');
  const [leadSearch, setLeadSearch] = useState('');
  const [mediaFiles, setMediaFiles] = useState<MediaFile[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const dispatch = useAppDispatch();
  const products = useSelector((state: RootState) => state.products.items);
  const productsLoading = useSelector((state: RootState) => state.products.loading);

  // Dados simulados de leads
  const [leads, setLeads] = useState<Lead[]>([
    { id: '1', name: 'João Silva', email: 'joao@example.com', phone: '(11) 98765-4321', source: 'Site', status: 'Novo' },
    { id: '2', name: 'Maria Oliveira', email: 'maria@example.com', phone: '(11) 91234-5678', source: 'Facebook', status: 'Contato Realizado' },
    { id: '3', name: 'Pedro Santos', email: 'pedro@example.com', phone: '(11) 99876-5432', source: 'Instagram', status: 'Interessado' },
    { id: '4', name: 'Ana Costa', email: 'ana@example.com', phone: '(11) 95555-4444', source: 'WhatsApp', status: 'Qualificado' },
    { id: '5', name: 'Carlos Ferreira', email: 'carlos@example.com', phone: '(11) 93333-2222', source: 'Indicação', status: 'Negociação' },
  ]);

  // Carregar campanhas do localStorage quando o componente montar
  useEffect(() => {
    const loadCampaigns = () => {
      try {
        const savedCampaigns = localStorage.getItem('marketingCampaigns');
        if (savedCampaigns) {
          // Converte as strings JSON para objetos
          const parsedCampaigns = JSON.parse(savedCampaigns);
          
          // Converte as strings de data para objetos Date
          const formattedCampaigns = parsedCampaigns.map((campaign: any) => {
            // Garante que schedules seja um array
            const schedules = Array.isArray(campaign.schedules) ? campaign.schedules : [];
            
            return {
              ...campaign,
              // Converte as datas de agendamento
              schedules: schedules.map((schedule: any) => ({
                ...schedule,
                startDate: new Date(schedule.startDate),
                endDate: new Date(schedule.endDate)
              })),
              // Converte a data de criação
              createdAt: new Date(campaign.createdAt || Date.now())
            };
          });
          
          setCampaigns(formattedCampaigns);
          console.log(`Carregadas ${formattedCampaigns.length} campanhas do localStorage`);
        }
      } catch (error) {
        console.error('Erro ao carregar campanhas:', error);
      }
    };

    loadCampaigns();
  }, []);

  // Carregar produtos quando o componente montar
  useEffect(() => {
    // Verifica se já existem produtos carregados
    if (products.length === 0) {
      console.log('Carregando produtos do mockData service...');
      dispatch(fetchProductsAsync());
    } else {
      console.log('Produtos já carregados:', products.length);
    }
  }, [dispatch, products.length]);

  // Salvar campanhas no localStorage sempre que houver mudanças
  useEffect(() => {
    if (campaigns.length > 0) {
      try {
        // Prepara os dados para serialização
        const campaignsToSave = campaigns.map(campaign => ({
          ...campaign,
          schedules: campaign.schedules.map(schedule => ({
            ...schedule,
            startDate: schedule.startDate instanceof Date ? schedule.startDate.toISOString() : schedule.startDate,
            endDate: schedule.endDate instanceof Date ? schedule.endDate.toISOString() : schedule.endDate
          })),
          createdAt: campaign.createdAt instanceof Date ? campaign.createdAt.toISOString() : campaign.createdAt
        }));
        
        // Salva no localStorage
        localStorage.setItem('marketingCampaigns', JSON.stringify(campaignsToSave));
      } catch (error) {
        console.error('Erro ao salvar campanhas no localStorage:', error);
      }
    }
  }, [campaigns]);

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    
    if (!newCampaign.name.trim()) {
      setSnackbarMessage('Por favor, informe o nome da campanha');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    if (!newCampaign.content.trim()) {
      setSnackbarMessage('Por favor, adicione conteúdo à sua campanha');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    if (newCampaign.schedules.length === 0) {
      setSnackbarMessage('Por favor, adicione pelo menos uma data de agendamento');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }

    // Cria uma nova campanha
    const newCampaignWithId: MarketingCampaign = {
      ...newCampaign,
      id: Math.random().toString(36).substr(2, 9),
      status: 'agendado'
    };

    // Adiciona a nova campanha à lista
    setCampaigns([...campaigns, newCampaignWithId]);
    
    // Salva a campanha no histórico (localStorage para SocialPostScheduler)
    try {
      // Primeiro, carrega as postagens existentes
      const savedPosts = localStorage.getItem('socialPosts');
      let existingPosts = [];
      
      if (savedPosts) {
        existingPosts = JSON.parse(savedPosts);
      }
      
      // Cria um objeto de postagem no formato esperado pelo SocialPostScheduler
      const socialPost: any = {
        id: newCampaignWithId.id,
        content: `[Campanha de Marketing] ${newCampaignWithId.name}\n\n${newCampaignWithId.content}`,
        platforms: [newCampaignWithId.type === 'email' ? 'Email' : 'WhatsApp'],
        platformTypes: {},
        schedules: newCampaignWithId.schedules.map(schedule => ({
          startDate: schedule.startDate instanceof Date ? schedule.startDate.toISOString() : schedule.startDate,
          endDate: schedule.endDate instanceof Date ? schedule.endDate.toISOString() : schedule.endDate
        })),
        status: 'agendado',
        platformStatus: {},
        platformMedia: {},
        createdAt: new Date().toISOString()
      };
      
      // Adiciona informações de produto se existir
      if (newCampaignWithId.product) {
        socialPost.product = {
          id: newCampaignWithId.product.id,
          name: newCampaignWithId.product.name,
          price: newCampaignWithId.product.price,
          description: newCampaignWithId.product.description,
          imageUrl: newCampaignWithId.product.imageUrl
        };
      }
      
      // Adiciona a nova postagem à lista
      existingPosts.push(socialPost);
      
      // Salva a lista atualizada no localStorage
      localStorage.setItem('socialPosts', JSON.stringify(existingPosts));
      
      // Dispara o evento customizado para notificar outros componentes
      window.dispatchEvent(new CustomEvent('socialPostsUpdated', { detail: existingPosts }));
      
      console.log('Campanha adicionada ao histórico de postagens');
    } catch (error) {
      console.error('Erro ao salvar campanha no histórico:', error);
    }

    // Limpa o formulário completamente
    setNewCampaign({
      name: '',
      type: 'email',
      content: '',
      schedules: [{ startDate: new Date(), endDate: new Date() }],
      createdAt: new Date(),
      mediaFiles: []
    });
    
    // Limpa a lista de mídias
    setMediaFiles([]);
    
    // Limpa a lista de leads selecionados
    setSelectedLeads([]);
    
    // Limpa o input de arquivo, se existir
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }

    // Mostra mensagem de sucesso
    setSnackbarMessage('Campanha agendada com sucesso e adicionada ao histórico!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    
    console.log('Formulário limpo após agendamento da campanha');
  };

  const handleDateChange = (date: Date | null, index: number) => {
    if (!date) return;

    const updatedSchedules = [...newCampaign.schedules];
    updatedSchedules[index] = {
      ...updatedSchedules[index],
      startDate: date,
      endDate: date
    };

    setNewCampaign({
      ...newCampaign,
      schedules: updatedSchedules
    });
  };

  const handleAddSchedule = () => {
    setNewCampaign({
      ...newCampaign,
      schedules: [...newCampaign.schedules, { startDate: new Date(), endDate: new Date() }]
    });
  };

  const handleRemoveSchedule = (index: number) => {
    const updatedSchedules = newCampaign.schedules.filter((_, i) => i !== index);
    setNewCampaign({
      ...newCampaign,
      schedules: updatedSchedules
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    (product.description && product.description.toLowerCase().includes(productSearch.toLowerCase()))
  );

  const filteredLeads = leads.filter(lead =>
    lead.name.toLowerCase().includes(leadSearch.toLowerCase()) ||
    lead.email.toLowerCase().includes(leadSearch.toLowerCase()) ||
    lead.phone.includes(leadSearch)
  );

  const handleSelectProduct = (product: any) => {
    setNewCampaign({
      ...newCampaign,
      product: {
        id: product.id,
        name: product.name,
        price: product.price,
        description: product.description,
        imageUrl: product.imageUrl
      },
      content: newCampaign.content + `\n\n🛍️ Produto: ${product.name}\n💰 Preço: R$ ${product.price.toFixed(2)}\n${product.description ? `📝 ${product.description}` : ''}`
    });
    
    setOpenProductDialog(false);
    
    setSnackbarMessage('Produto adicionado à campanha!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const handleSelectLeads = (selectedLeads: Lead[]) => {
    setNewCampaign({
      ...newCampaign,
      leads: selectedLeads
    });
    
    setOpenLeadDialog(false);
    
    setSnackbarMessage(`${selectedLeads.length} leads selecionados para a campanha!`);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };

  const [selectedLeads, setSelectedLeads] = useState<string[]>([]);

  const handleToggleLead = (leadId: string) => {
    const currentIndex = selectedLeads.indexOf(leadId);
    const newSelectedLeads = [...selectedLeads];

    if (currentIndex === -1) {
      newSelectedLeads.push(leadId);
    } else {
      newSelectedLeads.splice(currentIndex, 1);
    }

    setSelectedLeads(newSelectedLeads);
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;
    
    const file = event.target.files[0];
    
    // Verificar se é uma imagem ou vídeo
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      setSnackbarMessage('Por favor, selecione apenas arquivos de imagem ou vídeo.');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    
    // Criar URL para preview
    const fileUrl = URL.createObjectURL(file);
    
    // Criar objeto de mídia
    const newMedia: MediaFile = {
      id: Math.random().toString(36).substr(2, 9),
      file,
      type: file.type.startsWith('image/') ? 'image' : 'video',
      preview: fileUrl
    };
    
    // Adicionar à lista de mídias
    setMediaFiles([...mediaFiles, newMedia]);
    
    // Adicionar ao estado da campanha
    setNewCampaign(prev => ({
      ...prev,
      mediaFiles: [...(prev.mediaFiles || []), newMedia]
    }));
    
    // Limpar input de arquivo
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    setSnackbarMessage('Mídia adicionada com sucesso!');
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
  };
  
  const handleRemoveMedia = (id: string) => {
    // Remover da lista de mídias
    const updatedMediaFiles = mediaFiles.filter(media => media.id !== id);
    setMediaFiles(updatedMediaFiles);
    
    // Remover do estado da campanha
    setNewCampaign(prev => ({
      ...prev,
      mediaFiles: prev.mediaFiles?.filter(media => media.id !== id)
    }));
    
    setSnackbarMessage('Mídia removida com sucesso!');
    setSnackbarSeverity('info');
    setSnackbarOpen(true);
  };

  return (
    <Box>
      <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Nova Campanha de Marketing
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Nome da Campanha"
                value={newCampaign.name}
                onChange={(e) => setNewCampaign({ ...newCampaign, name: e.target.value })}
                required
                InputProps={{
                  startAdornment: <CampaignIcon sx={{ mr: 1, color: 'primary.main' }} />,
                }}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <FormControl component="fieldset">
                <Typography variant="subtitle2" gutterBottom>
                  Tipo de Campanha
                </Typography>
                <RadioGroup
                  row
                  value={newCampaign.type}
                  onChange={(e) => setNewCampaign({ ...newCampaign, type: e.target.value as 'email' | 'whatsapp' })}
                >
                  <FormControlLabel 
                    value="email" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <EmailIcon sx={{ mr: 0.5, color: 'primary.main' }} />
                        <Typography>E-mail</Typography>
                      </Box>
                    } 
                  />
                  <FormControlLabel 
                    value="whatsapp" 
                    control={<Radio />} 
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <WhatsAppIcon sx={{ mr: 0.5, color: 'green' }} />
                        <Typography>WhatsApp</Typography>
                      </Box>
                    } 
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<ShoppingCartIcon />}
                onClick={() => setOpenProductDialog(true)}
                sx={{ mb: 2 }}
              >
                Adicionar Produto
              </Button>
              
              {newCampaign.product && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {newCampaign.product.imageUrl && (
                        <Avatar 
                          src={newCampaign.product.imageUrl} 
                          variant="rounded" 
                          sx={{ width: 50, height: 50, mr: 2 }}
                        />
                      )}
                      <Box>
                        <Typography variant="subtitle2">{newCampaign.product.name}</Typography>
                        <Typography variant="body2" color="primary">
                          R$ {newCampaign.product.price.toFixed(2)}
                        </Typography>
                      </Box>
                      <IconButton 
                        size="small" 
                        color="error" 
                        sx={{ ml: 'auto' }}
                        onClick={() => {
                          setNewCampaign({
                            ...newCampaign,
                            product: undefined
                          });
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </CardContent>
                </Card>
              )}
              
              <Button
                variant="outlined"
                fullWidth
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={{ mb: 2 }}
              >
                Adicionar Mídia
                <input
                  type="file"
                  hidden
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  accept="image/*,video/*"
                />
              </Button>
              
              <Typography variant="caption" sx={{ display: 'block', mb: 2, color: 'text.secondary' }}>
                Suporta imagens e vídeos (uma mídia por vez)
              </Typography>
              
              {mediaFiles.length > 0 && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                  {mediaFiles.map((media) => (
                    <Card key={media.id} variant="outlined">
                      {media.type === 'image' ? (
                        <CardMedia
                          component="img"
                          height="140"
                          image={media.preview}
                          sx={{ objectFit: 'cover' }}
                        />
                      ) : (
                        <CardMedia
                          component="video"
                          height="140"
                          src={media.preview}
                          controls
                          sx={{ objectFit: 'cover' }}
                        />
                      )}
                      <CardContent sx={{ py: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {media.type === 'image' ? <PhotoCameraIcon fontSize="small" sx={{ mr: 1 }} /> : <VideocamIcon fontSize="small" sx={{ mr: 1 }} />}
                            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                              {media.file.name}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveMedia(media.id)}
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>
              )}
            </Grid>

            <Grid item xs={12} md={6}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<PeopleIcon />}
                onClick={() => {
                  setSelectedLeads(newCampaign.leads ? newCampaign.leads.map(lead => lead.id) : []);
                  setOpenLeadDialog(true);
                }}
                sx={{ mb: 2 }}
              >
                Selecionar Leads
              </Button>
              
              {newCampaign.leads && newCampaign.leads.length > 0 && (
                <Card variant="outlined" sx={{ mb: 2 }}>
                  <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle2">
                        {newCampaign.leads.length} leads selecionados
                      </Typography>
                      <IconButton 
                        size="small" 
                        color="error" 
                        sx={{ ml: 'auto' }}
                        onClick={() => {
                          setNewCampaign({
                            ...newCampaign,
                            leads: undefined
                          });
                        }}
                      >
                        <CloseIcon />
                      </IconButton>
                    </Box>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {newCampaign.leads.slice(0, 5).map(lead => (
                        <Chip 
                          key={lead.id} 
                          label={lead.name} 
                          size="small" 
                          variant="outlined"
                        />
                      ))}
                      {newCampaign.leads.length > 5 && (
                        <Chip 
                          label={`+${newCampaign.leads.length - 5} mais`} 
                          size="small" 
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label="Conteúdo da Campanha"
                value={newCampaign.content}
                onChange={(e) => setNewCampaign({ ...newCampaign, content: e.target.value })}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <Accordion>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls="ai-assistant-content"
                  id="ai-assistant-header"
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <SmartToyIcon sx={{ mr: 1 }} />
                    <Typography>Assistente IA - Gerador de Conteúdo</Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails>
                  <AIAssistant />
                </AccordionDetails>
              </Accordion>
            </Grid>

            <Grid item xs={12}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mr: 2 }}>
                  Datas de Agendamento
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleAddSchedule}
                  startIcon={<ScheduleIcon />}
                >
                  Adicionar Data
                </Button>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
                  {newCampaign.schedules.map((schedule, index) => (
                    <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <DateTimePicker
                        label="Data e Hora"
                        value={schedule.startDate}
                        onChange={(date) => handleDateChange(date, index)}
                        sx={{ flex: 1 }}
                      />
                      {newCampaign.schedules.length > 1 && (
                        <Button
                          variant="outlined"
                          color="error"
                          size="small"
                          onClick={() => handleRemoveSchedule(index)}
                          startIcon={<CloseIcon />}
                        >
                          Remover
                        </Button>
                      )}
                    </Box>
                  ))}
                </LocalizationProvider>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Button type="submit" variant="contained" color="primary" fullWidth>
                Agendar Campanha
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {campaigns.length > 0 && (
        <Box>
          <Typography variant="h6" gutterBottom>
            Campanhas Agendadas
          </Typography>
          <Grid container spacing={3}>
            {campaigns.map((campaign) => (
              <Grid item xs={12} md={6} lg={4} key={campaign.id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <CampaignIcon color="primary" sx={{ mr: 1 }} />
                      <Typography variant="h6">{campaign.name}</Typography>
                      <Chip
                        label={campaign.type === 'email' ? 'E-mail' : 'WhatsApp'}
                        icon={campaign.type === 'email' ? <EmailIcon /> : <WhatsAppIcon />}
                        color={campaign.type === 'email' ? 'primary' : 'success'}
                        size="small"
                        sx={{ ml: 'auto' }}
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {campaign.content.length > 100 
                        ? `${campaign.content.substring(0, 100)}...` 
                        : campaign.content}
                    </Typography>
                    
                    {campaign.product && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <ShoppingCartIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2">
                          Produto: {campaign.product.name}
                        </Typography>
                      </Box>
                    )}
                    
                    {campaign.leads && campaign.leads.length > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <PeopleIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="body2">
                          {campaign.leads.length} leads selecionados
                        </Typography>
                      </Box>
                    )}
                    
                    <Typography variant="subtitle2" gutterBottom>
                      Agendamentos:
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {campaign.schedules.map((schedule, index) => (
                        <Chip
                          key={index}
                          label={format(schedule.startDate, 'dd/MM/yyyy HH:mm')}
                          size="small"
                          variant="outlined"
                          color="primary"
                          icon={<ScheduleIcon />}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Diálogo de seleção de produto */}
      <Dialog
        open={openProductDialog}
        onClose={() => setOpenProductDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Selecionar Produto</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Buscar produtos..."
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <List>
            {filteredProducts.map((product) => (
              <ListItem 
                key={product.id}
                button
                onClick={() => handleSelectProduct(product)}
                sx={{
                  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                  '&:last-child': { borderBottom: 'none' }
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    variant="rounded"
                    src={product.imageUrl}
                    alt={product.name}
                    sx={{ width: 60, height: 60, mr: 2 }}
                  />
                </ListItemAvatar>
                <ListItemText
                  primary={product.name}
                  secondary={
                    <Box>
                      <Typography variant="body2">
                        Preço: R$ {product.price.toFixed(2)}
                      </Typography>
                      {product.description && (
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {product.description}
                        </Typography>
                      )}
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProductDialog(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de seleção de leads */}
      <Dialog
        open={openLeadDialog}
        onClose={() => setOpenLeadDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Selecionar Leads</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            placeholder="Buscar leads..."
            value={leadSearch}
            onChange={(e) => setLeadSearch(e.target.value)}
            sx={{ mb: 2, mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <List>
            {filteredLeads.map((lead) => (
              <ListItem 
                key={lead.id}
                button
                onClick={() => handleToggleLead(lead.id)}
                selected={selectedLeads.indexOf(lead.id) !== -1}
                sx={{
                  borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                  '&:last-child': { borderBottom: 'none' },
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(25, 118, 210, 0.08)',
                  }
                }}
              >
                <ListItemAvatar>
                  <Avatar>
                    {lead.name.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={lead.name}
                  secondary={
                    <Box>
                      <Typography variant="body2">
                        {lead.email}
                      </Typography>
                      <Typography variant="body2">
                        {lead.phone} • {lead.source} • {lead.status}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLeadDialog(false)}>Cancelar</Button>
          <Button 
            onClick={() => {
              const selectedLeadObjects = leads.filter(lead => selectedLeads.includes(lead.id));
              handleSelectLeads(selectedLeadObjects);
            }}
            variant="contained"
            disabled={selectedLeads.length === 0}
          >
            Selecionar ({selectedLeads.length})
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MarketingScheduler;
